import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ClientAnswer, TemplateQuestion } from '@/api/orderDetails'
import {
  formatSurveyAnswerText,
  getSurveyImageFileInfo,
  getSurveyLocationDisplay,
} from '@/utils/orderSurveyDisplay'
import { GeocodedLocationLink } from '@/components/shared/GeocodedLocationLink'
import {
  getDisplayNameForCode,
  getDisplayNameForInstanceOrServiceLabel,
} from '@/utils/surveyQuestionDisplayNames'

interface SurveyQuestionDisplayProps {
  answer: ClientAnswer
  locale: 'en' | 'ar'
  templateQuestionsForLookup?: TemplateQuestion[]
}

function findQuestionLabel(
  answer: ClientAnswer,
  templateQuestions: TemplateQuestion[] | undefined,
  locale: 'en' | 'ar'
): string | null {
  if (!templateQuestions?.length) return null
  const code = answer.questionCode?.trim().toLowerCase()
  const templateId = answer.templateQuestionId?.trim()
  const match = templateQuestions.find(
    (tq) =>
      (code && tq.question?.code?.trim().toLowerCase() === code) ||
      (templateId && tq.id === templateId)
  )
  if (!match?.question) return null
  const q = match.question
  if (locale === 'ar') return q.textAr ?? q.textEn ?? q.code
  return q.textEn ?? q.textAr ?? q.code
}

export function SurveyQuestionDisplay({
  answer,
  locale,
  templateQuestionsForLookup,
}: SurveyQuestionDisplayProps) {
  const [imageOpen, setImageOpen] = useState(false)
  const imageInfo = getSurveyImageFileInfo(answer.answer, answer.rawAnswer)
  const locationDisplay = !imageInfo
    ? getSurveyLocationDisplay(
        answer.answer,
        answer.rawAnswer,
        answer.questionType,
        answer.questionCode,
        locale
      )
    : null
  const formattedText = !imageInfo && !locationDisplay
    ? formatSurveyAnswerText(answer.answer, answer.rawAnswer, answer.questionType, locale)
    : null

  const isCodeOnly =
    answer.questionCode &&
    answer.questionText?.trim().toLowerCase() === answer.questionCode.trim().toLowerCase()

  const resolvedLabel =
    isCodeOnly && templateQuestionsForLookup?.length
      ? findQuestionLabel(answer, templateQuestionsForLookup, locale)
      : null

  const codeDisplayNameFallback =
    isCodeOnly && !resolvedLabel && answer.questionCode
      ? getDisplayNameForCode(answer.questionCode, locale)
      : null

  const instanceDisplayName =
    answer.instanceLabel != null
      ? getDisplayNameForInstanceOrServiceLabel(answer.instanceLabel, locale) ?? answer.instanceLabel
      : null

  const questionTitleDisplay =
    answer.questionText != null
      ? getDisplayNameForInstanceOrServiceLabel(answer.questionText, locale) ?? answer.questionText
      : ''

  const questionTitle =
    isCodeOnly
      ? resolvedLabel ?? codeDisplayNameFallback ?? answer.questionCode
      : questionTitleDisplay || answer.questionCode

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {answer.questionType && (
          <Badge variant="secondary" className="text-xs font-normal border-0 bg-white text-gray-600">
            {answer.questionType}
          </Badge>
        )}
        {instanceDisplayName && (
          <span className="text-xs text-gray-500">{instanceDisplayName}</span>
        )}
      </div>
      <p className="text-sm font-medium text-gray-900 mb-2">{questionTitle}</p>
      <div className="text-sm text-gray-800 bg-white rounded-md border border-gray-100 px-3 py-2.5 min-h-[2.25rem]">
        {imageInfo ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setImageOpen(true)}
              className="rounded-md border border-gray-200 overflow-hidden text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              <img
                src={imageInfo.url}
                alt={imageInfo.name || 'upload'}
                className="max-w-full max-h-48 object-contain cursor-pointer hover:opacity-90"
                loading="lazy"
              />
            </button>
            <a
              href={imageInfo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary hover:underline text-sm w-fit"
            >
              {locale === 'ar' ? 'تحميل' : 'Download'}
            </a>
            <Dialog open={imageOpen} onOpenChange={setImageOpen}>
              <DialogContent className="max-w-[90vw] max-h-[90vh] w-auto p-2">
                <DialogTitle className="sr-only">{imageInfo.name || 'Image'}</DialogTitle>
                <img
                  src={imageInfo.url}
                  alt={imageInfo.name || 'Image'}
                  className="max-w-full max-h-[85vh] object-contain rounded"
                />
              </DialogContent>
            </Dialog>
          </div>
        ) : locationDisplay ? (
          <GeocodedLocationLink
            location={locationDisplay.label}
            href={locationDisplay.href}
            lat={locationDisplay.coords?.lat}
            lng={locationDisplay.coords?.lng}
            className="text-sm font-medium"
          />
        ) : (
          formattedText
        )}
      </div>
    </div>
  )
}
